/**
 * Holds the session — CLAUDE.md §6.7. The token lives in `sessionStorage` for the life of the tab,
 * never `localStorage` and never a cookie (see `lib/api.ts`). A restored token is not trusted on
 * sight: it seeds `restoring`, and `/auth/me` re-verifies it against Base Portal.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  fetchCurrentUser,
  getAuthToken,
  login,
  logout,
  setAuthToken,
  type AuthUser,
  type Credentials,
} from '@/lib/api';
import {
  AuthContext,
  basePortalHandoff,
  clearBasePortalHandoff,
  type AuthContextValue,
  type AuthStatus,
} from '@/lib/auth';
import { setSessionLoginFactory } from '@/reports/production-monthly-output/filters';

const CURRENT_USER_KEY = ['auth', 'me'] as const;

/**
 * Did Base Portal reject the credential, or could it simply not be asked? Only 401 and 403 are
 * decisions; treating a 503 as a bad password sends someone to reset a working one (§6.7).
 */
function isCredentialRejected(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.problem.status === 401 || error.problem.status === 403)
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // The handoff `main.tsx` took out of the URL, else the token this tab already carries. A handoff
  // wins: a fresh Base Portal token supersedes whatever was held.
  const [token, setToken] = useState<string | null>(
    () => basePortalHandoff()?.token ?? getAuthToken(),
  );
  const [signedInUser, setSignedInUser] = useState<AuthUser | null>(null);
  const [handoffLoginName, setHandoffLoginName] = useState<string | null>(
    () => basePortalHandoff()?.loginName ?? null,
  );

  /**
   * Restores session state when a token is in hand but the identity behind it is not. Not retried
   * — a failure here is an answer, and hammering it is the retry storm §6.7 rules out.
   */
  const restore = useQuery({
    queryKey: CURRENT_USER_KEY,
    queryFn: fetchCurrentUser,
    enabled: token !== null && signedInUser === null,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const user = signedInUser ?? restore.data ?? null;

  // Synchronous, not an effect: an effect runs after children mount, so the first filter parse and
  // first query would already have fallen back to the default factory.
  setSessionLoginFactory(user?.defaultFactory ?? null);

  let status: AuthStatus;
  if (user !== null) {
    status = 'authenticated';
  } else if (token === null) {
    status = 'anonymous';
  } else if (restore.isError) {
    status = isCredentialRejected(restore.error) ? 'anonymous' : 'unavailable';
  } else {
    status = 'restoring';
  }

  const signIn = useCallback(
    async (credentials: Credentials) => {
      // A rejected token must not ride along on the one request meant to replace it.
      setAuthToken(null);
      queryClient.clear();

      const session = await login(credentials);

      setAuthToken(session.token);
      setToken(session.token);
      setSignedInUser(session.user);
      setSessionLoginFactory(session.user.defaultFactory);
      setHandoffLoginName(null);
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await logout();
    } catch {
      // No server-side session to end (§6.7) — the token is discarded either way.
    }

    setAuthToken(null);
    clearBasePortalHandoff();
    setToken(null);
    setSignedInUser(null);
    setSessionLoginFactory(null);
    setHandoffLoginName(null);

    // The in-memory cache survives a route change; evicting stops the next person at this terminal
    // seeing the last person's figures (§6.3).
    queryClient.clear();
  }, [queryClient]);

  const { refetch } = restore;
  const retryRestore = useCallback(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, handoffLoginName, signIn, signOut, retryRestore }),
    [status, user, handoffLoginName, signIn, signOut, retryRestore],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
